import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  Building2,
  Users,
  Users2,
  FileText,
  FolderOpen,
  HardDrive,
  Calendar,
  Globe,
  Edit,
  Trash2,
  Power,
  PowerOff,
  MoreHorizontal,
  Crown,
  AlertTriangle,
  Mail,
  Phone,
  MapPin,
  CreditCard,
  Activity,
  Clock,
  File,
  Image,
  ChevronRight,
  ExternalLink,
  Loader2,
  ShieldAlert
} from 'lucide-react'
import { useAuth } from '@/lib/contexts/auth-context'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { Skeleton } from '@/components/ui/skeleton'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import {
  tenantsService,
  type TenantDashboard,
  type UpdateTenantRequest
} from '@/lib/api/services/tenants'
import { useToast } from '@/components/ui/use-toast'
import { cn } from '@/lib/utils'
import { getAvatarColor } from '@/lib/avatar-colors'

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

function formatDateTime(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

const planColors: Record<string, string> = {
  Free: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300',
  Basic: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
  Professional: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
  Enterprise: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300',
}

const typeIcons: Record<string, typeof FileText> = {
  Word: FileText,
  Excel: File,
  PowerPoint: File,
  Image: Image,
}

export default function CompanyDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()
  const { hasPermission, isLoading: authLoading } = useAuth()

  // Check if user has system.admin permission (super admin only)
  const canAccessPage = hasPermission('system.admin')

  const [dashboard, setDashboard] = useState<TenantDashboard | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [editContactDialogOpen, setEditContactDialogOpen] = useState(false)
  const [editBillingDialogOpen, setEditBillingDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [forceDeleteDialogOpen, setForceDeleteDialogOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  // Show access denied if user doesn't have permission
  if (!canAccessPage) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
        <div className="rounded-full bg-destructive/10 p-4 mb-4">
          <ShieldAlert className="h-12 w-12 text-destructive" />
        </div>
        <h1 className="text-2xl font-bold mb-2">Access Denied</h1>
        <p className="text-muted-foreground max-w-md mb-6">
          You don't have permission to access Company details. Please contact your administrator if you need access.
        </p>
        <Button variant="outline" onClick={() => window.history.back()}>
          Go Back
        </Button>
      </div>
    )
  }

  // Edit form state
  const [editForm, setEditForm] = useState({
    name: '',
    planType: '',
    isActive: true
  })

  // Contact form state
  const [contactForm, setContactForm] = useState({
    companyEmail: '',
    companyPhone: '',
    companyWebsite: '',
    companyAddress: '',
    companyCity: '',
    companyState: '',
    companyCountry: '',
    companyPostalCode: ''
  })

  // Billing form state
  const [billingForm, setBillingForm] = useState({
    billingEmail: '',
    billingAddress: '',
    taxId: ''
  })

  useEffect(() => {
    if (id) {
      loadDashboard()
    }
  }, [id])

  const loadDashboard = async () => {
    if (!id) return

    try {
      setLoading(true)
      setError(null)
      const data = await tenantsService.getTenantDashboard(id)
      setDashboard(data)
      setEditForm({
        name: data.tenant.name,
        planType: data.tenant.planType,
        isActive: data.tenant.isActive
      })
      setContactForm({
        companyEmail: data.tenant.companyEmail || '',
        companyPhone: data.tenant.companyPhone || '',
        companyWebsite: data.tenant.companyWebsite || '',
        companyAddress: data.tenant.companyAddress || '',
        companyCity: data.tenant.companyCity || '',
        companyState: data.tenant.companyState || '',
        companyCountry: data.tenant.companyCountry || '',
        companyPostalCode: data.tenant.companyPostalCode || ''
      })
      setBillingForm({
        billingEmail: data.tenant.billingEmail || '',
        billingAddress: data.tenant.billingAddress || '',
        taxId: data.tenant.taxId || ''
      })
    } catch (err) {
      console.error('Failed to load dashboard:', err)
      setError('Failed to load company details')
    } finally {
      setLoading(false)
    }
  }

  const handleUpdate = async () => {
    if (!dashboard) return

    try {
      setIsSubmitting(true)
      const updateData: UpdateTenantRequest = {
        id: dashboard.tenant.id,
        name: editForm.name,
        planType: editForm.planType,
        isActive: editForm.isActive
      }
      await tenantsService.updateTenant(dashboard.tenant.id, updateData)
      toast({ title: 'Success', description: 'Company updated successfully' })
      setEditDialogOpen(false)
      loadDashboard()
    } catch (err) {
      console.error('Failed to update tenant:', err)
      toast({ title: 'Error', description: 'Failed to update company', variant: 'destructive' })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleToggleActive = async () => {
    if (!dashboard) return

    try {
      setIsSubmitting(true)
      const updateData: UpdateTenantRequest = {
        id: dashboard.tenant.id,
        name: dashboard.tenant.name,
        isActive: !dashboard.tenant.isActive
      }
      await tenantsService.updateTenant(dashboard.tenant.id, updateData)
      toast({
        title: 'Success',
        description: dashboard.tenant.isActive ? 'Company deactivated' : 'Company activated'
      })
      loadDashboard()
    } catch (err) {
      console.error('Failed to toggle tenant status:', err)
      toast({ title: 'Error', description: 'Failed to update company status', variant: 'destructive' })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDelete = async (forceDelete: boolean = false) => {
    if (!dashboard) return

    try {
      setIsSubmitting(true)
      await tenantsService.deleteTenant(dashboard.tenant.id, forceDelete)
      toast({
        title: 'Success',
        description: forceDelete ? 'Company permanently deleted' : 'Company deactivated'
      })
      navigate('/admin/companies')
    } catch (err) {
      console.error('Failed to delete tenant:', err)
      toast({ title: 'Error', description: 'Failed to delete company', variant: 'destructive' })
    } finally {
      setIsSubmitting(false)
      setDeleteDialogOpen(false)
      setForceDeleteDialogOpen(false)
    }
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10" />
          <div className="space-y-2">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-4 w-48" />
          </div>
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-64" />
          ))}
        </div>
      </div>
    )
  }

  if (error || !dashboard) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <AlertTriangle className="h-12 w-12 text-yellow-500 mb-4" />
        <h2 className="text-xl font-semibold mb-2">Company Not Found</h2>
        <p className="text-muted-foreground mb-4">{error || 'The requested company could not be found.'}</p>
        <Button onClick={() => navigate('/admin/companies')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Companies
        </Button>
      </div>
    )
  }

  const { tenant, storage, usage, topUsers, topTemplates, recentAssets, teams } = dashboard

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/admin/companies')}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
              <Building2 className="h-6 w-6 text-primary" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold">{tenant.name}</h1>
                <Badge
                  variant={tenant.isActive ? 'default' : 'secondary'}
                  className={tenant.isActive ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' : ''}
                >
                  {tenant.isActive ? 'Active' : 'Inactive'}
                </Badge>
                <Badge className={planColors[tenant.planType] || planColors.Basic}>
                  <Crown className="mr-1 h-3 w-3" />
                  {tenant.planType}
                </Badge>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Globe className="h-4 w-4" />
                <span>{tenant.subdomain}.templatean.com</span>
              </div>
            </div>
          </div>
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline">
              <MoreHorizontal className="mr-2 h-4 w-4" />
              Actions
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => setEditDialogOpen(true)}>
              <Edit className="mr-2 h-4 w-4" />
              Edit Company
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleToggleActive}>
              {tenant.isActive ? (
                <>
                  <PowerOff className="mr-2 h-4 w-4" />
                  Deactivate
                </>
              ) : (
                <>
                  <Power className="mr-2 h-4 w-4" />
                  Activate
                </>
              )}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => setDeleteDialogOpen(true)}
              className="text-red-600 focus:text-red-600"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete Company
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Stats Overview */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Users</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{usage.totalUsers}</div>
            <p className="text-xs text-muted-foreground">
              {usage.activeUsers} active
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Templates</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{usage.totalTemplates}</div>
            <p className="text-xs text-muted-foreground">
              {usage.publishedTemplates} published
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Assets</CardTitle>
            <FolderOpen className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{usage.totalAssets}</div>
            <p className="text-xs text-muted-foreground">
              {usage.totalDocumentsGenerated} documents generated
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Storage</CardTitle>
            <HardDrive className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatBytes(storage.usedBytes)}</div>
            <p className="text-xs text-muted-foreground mb-2">
              of {formatBytes(storage.limitBytes)} ({storage.usagePercentage.toFixed(1)}%)
            </p>
            <Progress
              value={storage.usagePercentage}
              className={cn(
                "h-2",
                storage.usagePercentage > 90 ? "[&>div]:bg-red-500" :
                storage.usagePercentage > 75 ? "[&>div]:bg-yellow-500" : ""
              )}
            />
          </CardContent>
        </Card>
      </div>

      {/* Main Content Grid */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        {/* Top Users */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="h-5 w-5" />
                Top Users
              </CardTitle>
              <CardDescription>Recently active users</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" asChild>
                <Link to={`/admin/users?tenantId=${id}`}>
                  View All
                  <ExternalLink className="ml-1 h-3 w-3" />
                </Link>
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {topUsers.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No users found</p>
            ) : (
              <div className="space-y-1">
                {topUsers.map((user) => (
                  <Link
                    key={user.id}
                    to={`/admin/users?tenantId=${id}&userId=${user.id}`}
                    className="flex items-center gap-3 p-2 -mx-2 rounded-lg hover:bg-muted/50 transition-colors group"
                  >
                    <Avatar className="h-9 w-9">
                      <AvatarFallback className={`${getAvatarColor(user.id || user.email).bg} ${getAvatarColor(user.id || user.email).text} text-xs`}>
                        {getInitials(user.fullName)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate group-hover:text-primary transition-colors">{user.fullName}</p>
                      <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                    </div>
                    <div className="text-right flex items-center gap-2">
                      <div>
                        <Badge variant="outline" className="text-xs">{user.role}</Badge>
                        {user.lastLoginAt && (
                          <p className="text-xs text-muted-foreground mt-1">
                            {formatDate(user.lastLoginAt)}
                          </p>
                        )}
                      </div>
                      <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Templates */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Recent Templates
              </CardTitle>
              <CardDescription>Latest template activity</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" asChild>
                <Link to={`/templates?tenantId=${id}`}>
                  View All
                  <ExternalLink className="ml-1 h-3 w-3" />
                </Link>
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {topTemplates.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No templates found</p>
            ) : (
              <div className="space-y-1">
                {topTemplates.map((template) => {
                  const Icon = typeIcons[template.type] || FileText
                  return (
                    <Link
                      key={template.id}
                      to={`/templates/${template.id}`}
                      className="flex items-center gap-3 p-2 -mx-2 rounded-lg hover:bg-muted/50 transition-colors group"
                    >
                      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900/30">
                        <Icon className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate group-hover:text-primary transition-colors">{template.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {template.type} {template.category && `- ${template.category}`}
                        </p>
                      </div>
                      <div className="text-right flex items-center gap-2">
                        <p className="text-xs text-muted-foreground">
                          {formatDate(template.updatedAt)}
                        </p>
                        <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </Link>
                  )
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Assets */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <FolderOpen className="h-5 w-5" />
                Recent Assets
              </CardTitle>
              <CardDescription>Latest uploaded assets</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" asChild>
                <Link to={`/assets?tenantId=${id}`}>
                  View All
                  <ExternalLink className="ml-1 h-3 w-3" />
                </Link>
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {recentAssets.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No assets found</p>
            ) : (
              <div className="space-y-1">
                {recentAssets.map((asset) => (
                  <Link
                    key={asset.id}
                    to={`/assets?tenantId=${id}&assetId=${asset.id}`}
                    className="flex items-center gap-3 p-2 -mx-2 rounded-lg hover:bg-muted/50 transition-colors group"
                  >
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-green-100 dark:bg-green-900/30">
                      <Image className="h-4 w-4 text-green-600 dark:text-green-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate group-hover:text-primary transition-colors">{asset.name}</p>
                      <p className="text-xs text-muted-foreground">{asset.type}</p>
                    </div>
                    <div className="text-right flex items-center gap-2">
                      <div>
                        <p className="text-xs font-medium">{formatBytes(asset.size)}</p>
                        <p className="text-xs text-muted-foreground">
                          {formatDate(asset.createdAt)}
                        </p>
                      </div>
                      <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Teams */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <Users2 className="h-5 w-5" />
                Teams
              </CardTitle>
              <CardDescription>Organization teams</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" asChild>
                <Link to={`/admin/teams?tenantId=${id}`}>
                  View All
                  <ExternalLink className="ml-1 h-3 w-3" />
                </Link>
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {!teams || teams.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No teams found</p>
            ) : (
              <div className="space-y-1">
                {teams.map((team) => (
                  <Link
                    key={team.id}
                    to={`/admin/teams?tenantId=${id}&teamId=${team.id}`}
                    className="flex items-center gap-3 p-2 -mx-2 rounded-lg hover:bg-muted/50 transition-colors group"
                  >
                    <div
                      className="flex h-9 w-9 items-center justify-center rounded-full text-white font-semibold text-sm"
                      style={{ backgroundColor: team.color || '#6B7280' }}
                    >
                      {team.name.substring(0, 2).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate group-hover:text-primary transition-colors">{team.name}</p>
                      {team.description && (
                        <p className="text-xs text-muted-foreground truncate">{team.description}</p>
                      )}
                    </div>
                    <div className="text-right flex items-center gap-2">
                      <div>
                        <Badge variant="secondary" className="gap-1">
                          <Users className="h-3 w-3" />
                          {team.memberCount}
                        </Badge>
                      </div>
                      <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Company Details */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Company Information */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Company Information
            </CardTitle>
            <Button variant="outline" size="sm" onClick={() => setEditDialogOpen(true)}>
              <Edit className="mr-1 h-3 w-3" />
              Edit
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label className="text-muted-foreground text-xs">Company Name</Label>
                <p className="font-medium">{tenant.name}</p>
              </div>
              <div>
                <Label className="text-muted-foreground text-xs">Subdomain</Label>
                <p className="font-medium">{tenant.subdomain}</p>
              </div>
              <div>
                <Label className="text-muted-foreground text-xs">Plan</Label>
                <p className="font-medium">{tenant.planType}</p>
              </div>
              <div>
                <Label className="text-muted-foreground text-xs">Status</Label>
                <p className="font-medium">{tenant.isActive ? 'Active' : 'Inactive'}</p>
              </div>
              <div>
                <Label className="text-muted-foreground text-xs">Created</Label>
                <p className="font-medium flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {formatDateTime(tenant.createdAt)}
                </p>
              </div>
              <div>
                <Label className="text-muted-foreground text-xs">Last Updated</Label>
                <p className="font-medium flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {formatDateTime(tenant.updatedAt)}
                </p>
              </div>
            </div>

            {/* Contact Details */}
            <div className="pt-4 border-t">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold">Contact Details</h4>
                <Button variant="ghost" size="sm" onClick={() => setEditContactDialogOpen(true)}>
                  <Edit className="mr-1 h-3 w-3" />
                  Edit
                </Button>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  {tenant.companyEmail ? (
                    <a href={`mailto:${tenant.companyEmail}`} className="text-primary hover:underline">
                      {tenant.companyEmail}
                    </a>
                  ) : (
                    <span className="text-muted-foreground">Not set</span>
                  )}
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  {tenant.companyPhone ? (
                    <a href={`tel:${tenant.companyPhone}`} className="text-primary hover:underline">
                      {tenant.companyPhone}
                    </a>
                  ) : (
                    <span className="text-muted-foreground">Not set</span>
                  )}
                </div>
                <div className="flex items-center gap-2 text-sm sm:col-span-2">
                  <Globe className="h-4 w-4 text-muted-foreground" />
                  {tenant.companyWebsite ? (
                    <a href={tenant.companyWebsite} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline flex items-center gap-1">
                      {tenant.companyWebsite}
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  ) : (
                    <span className="text-muted-foreground">Not set</span>
                  )}
                </div>
              </div>
            </div>

            {/* Address */}
            <div className="pt-4 border-t">
              <h4 className="text-sm font-semibold mb-3">Address</h4>
              <div className="flex items-start gap-2 text-sm">
                <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                <div className="text-muted-foreground">
                  {tenant.companyAddress ? (
                    <>
                      <p>{tenant.companyAddress}</p>
                      <p>
                        {[tenant.companyCity, tenant.companyState, tenant.companyPostalCode]
                          .filter(Boolean)
                          .join(', ')}
                      </p>
                      <p>{tenant.companyCountry}</p>
                    </>
                  ) : (
                    <p>No address set</p>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Billing & Finance */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Billing & Finance
            </CardTitle>
            <Button variant="outline" size="sm" onClick={() => setEditBillingDialogOpen(true)}>
              <Edit className="mr-1 h-3 w-3" />
              Edit
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label className="text-muted-foreground text-xs">Billing Email</Label>
                <p className="font-medium">
                  {tenant.billingEmail ? (
                    <a href={`mailto:${tenant.billingEmail}`} className="text-primary hover:underline">
                      {tenant.billingEmail}
                    </a>
                  ) : (
                    <span className="text-muted-foreground">Not set</span>
                  )}
                </p>
              </div>
              <div>
                <Label className="text-muted-foreground text-xs">Tax ID</Label>
                <p className="font-medium">{tenant.taxId || <span className="text-muted-foreground">Not set</span>}</p>
              </div>
            </div>

            <div className="pt-4 border-t">
              <h4 className="text-sm font-semibold mb-3">Billing Address</h4>
              <div className="flex items-start gap-2 text-sm">
                <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                <span className="text-muted-foreground">
                  {tenant.billingAddress || 'Same as company address'}
                </span>
              </div>
            </div>

            {/* Storage Details */}
            <div className="pt-4 border-t">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold">Storage Usage</h4>
                <Button variant="ghost" size="sm" asChild>
                  <Link to={`/assets?tenantId=${id}`}>
                    Manage
                    <ChevronRight className="ml-1 h-3 w-3" />
                  </Link>
                </Button>
              </div>
              <div className="space-y-3">
                <Progress
                  value={storage.usagePercentage}
                  className={cn(
                    "h-2",
                    storage.usagePercentage > 90 ? "[&>div]:bg-red-500" :
                    storage.usagePercentage > 75 ? "[&>div]:bg-yellow-500" : ""
                  )}
                />
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Used</span>
                  <span className="font-medium">{formatBytes(storage.usedBytes)} of {formatBytes(storage.limitBytes)} ({storage.usagePercentage.toFixed(1)}%)</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Total Files</span>
                  <span className="font-medium">{storage.totalFiles}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Avg. File Size</span>
                  <span className="font-medium">{formatBytes(storage.averageFileSize)}</span>
                </div>
              </div>
            </div>

            {/* Last Activity */}
            {usage.lastActivityAt && (
              <div className="pt-4 border-t">
                <h4 className="text-sm font-semibold mb-3">Last Activity</h4>
                <div className="flex items-center gap-2 text-sm">
                  <Activity className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">
                    {formatDateTime(usage.lastActivityAt)}
                  </span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Company</DialogTitle>
            <DialogDescription>
              Update the company details below.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Company Name</Label>
              <Input
                id="name"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                placeholder="Enter company name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="planType">Plan</Label>
              <Select
                value={editForm.planType}
                onValueChange={(value) => setEditForm({ ...editForm, planType: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a plan" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Free">Free</SelectItem>
                  <SelectItem value="Basic">Basic</SelectItem>
                  <SelectItem value="Professional">Professional</SelectItem>
                  <SelectItem value="Enterprise">Enterprise</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="isActive">Active Status</Label>
                <p className="text-sm text-muted-foreground">
                  Enable or disable this company
                </p>
              </div>
              <Switch
                id="isActive"
                checked={editForm.isActive}
                onCheckedChange={(checked) => setEditForm({ ...editForm, isActive: checked })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdate} disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Company</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{tenant.name}</strong>?
              This will deactivate the company and all its users will lose access.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDelete(false)}
              className="bg-red-600 hover:bg-red-700"
            >
              Deactivate
            </AlertDialogAction>
            <AlertDialogAction
              onClick={() => {
                setDeleteDialogOpen(false)
                setForceDeleteDialogOpen(true)
              }}
              className="bg-red-600 hover:bg-red-700"
            >
              Permanently Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Force Delete Confirmation */}
      <AlertDialog open={forceDeleteDialogOpen} onOpenChange={setForceDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600">
              <AlertTriangle className="inline-block mr-2 h-5 w-5" />
              Permanently Delete Company
            </AlertDialogTitle>
            <AlertDialogDescription>
              <strong className="text-red-600">WARNING: This action cannot be undone!</strong>
              <br /><br />
              Permanently deleting <strong>{tenant.name}</strong> will:
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Delete all {usage.totalUsers} users</li>
                <li>Delete all {usage.totalTemplates} templates</li>
                <li>Delete all {usage.totalAssets} assets ({formatBytes(storage.usedBytes)})</li>
                <li>Remove all company data permanently</li>
              </ul>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDelete(true)}
              className="bg-red-600 hover:bg-red-700"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Deleting...' : 'Yes, Permanently Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Contact Dialog */}
      <Dialog open={editContactDialogOpen} onOpenChange={setEditContactDialogOpen}>
        <DialogContent className="sm:max-w-[525px]">
          <DialogHeader>
            <DialogTitle>Edit Contact Details</DialogTitle>
            <DialogDescription>
              Update the company contact information and address.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="companyEmail">Email</Label>
                <Input
                  id="companyEmail"
                  type="email"
                  value={contactForm.companyEmail}
                  onChange={(e) => setContactForm({ ...contactForm, companyEmail: e.target.value })}
                  placeholder="company@example.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="companyPhone">Phone</Label>
                <Input
                  id="companyPhone"
                  type="tel"
                  value={contactForm.companyPhone}
                  onChange={(e) => setContactForm({ ...contactForm, companyPhone: e.target.value })}
                  placeholder="+1 (555) 123-4567"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="companyWebsite">Website</Label>
              <Input
                id="companyWebsite"
                type="url"
                value={contactForm.companyWebsite}
                onChange={(e) => setContactForm({ ...contactForm, companyWebsite: e.target.value })}
                placeholder="https://www.example.com"
              />
            </div>
            <div className="pt-4 border-t">
              <h4 className="text-sm font-semibold mb-3">Address</h4>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="companyAddress">Street Address</Label>
                  <Input
                    id="companyAddress"
                    value={contactForm.companyAddress}
                    onChange={(e) => setContactForm({ ...contactForm, companyAddress: e.target.value })}
                    placeholder="123 Main Street"
                  />
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="companyCity">City</Label>
                    <Input
                      id="companyCity"
                      value={contactForm.companyCity}
                      onChange={(e) => setContactForm({ ...contactForm, companyCity: e.target.value })}
                      placeholder="New York"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="companyState">State / Province</Label>
                    <Input
                      id="companyState"
                      value={contactForm.companyState}
                      onChange={(e) => setContactForm({ ...contactForm, companyState: e.target.value })}
                      placeholder="NY"
                    />
                  </div>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="companyPostalCode">Postal Code</Label>
                    <Input
                      id="companyPostalCode"
                      value={contactForm.companyPostalCode}
                      onChange={(e) => setContactForm({ ...contactForm, companyPostalCode: e.target.value })}
                      placeholder="10001"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="companyCountry">Country</Label>
                    <Input
                      id="companyCountry"
                      value={contactForm.companyCountry}
                      onChange={(e) => setContactForm({ ...contactForm, companyCountry: e.target.value })}
                      placeholder="United States"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditContactDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => {
              // TODO: Implement contact update when backend supports it
              toast({ title: 'Info', description: 'Contact update is not yet implemented in the backend' })
              setEditContactDialogOpen(false)
            }}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Billing Dialog */}
      <Dialog open={editBillingDialogOpen} onOpenChange={setEditBillingDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Edit Billing Information</DialogTitle>
            <DialogDescription>
              Update the company billing details.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="billingEmail">Billing Email</Label>
              <Input
                id="billingEmail"
                type="email"
                value={billingForm.billingEmail}
                onChange={(e) => setBillingForm({ ...billingForm, billingEmail: e.target.value })}
                placeholder="billing@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="taxId">Tax ID / VAT Number</Label>
              <Input
                id="taxId"
                value={billingForm.taxId}
                onChange={(e) => setBillingForm({ ...billingForm, taxId: e.target.value })}
                placeholder="US123456789"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="billingAddress">Billing Address</Label>
              <Input
                id="billingAddress"
                value={billingForm.billingAddress}
                onChange={(e) => setBillingForm({ ...billingForm, billingAddress: e.target.value })}
                placeholder="Leave empty to use company address"
              />
              <p className="text-xs text-muted-foreground">
                Leave empty to use the same address as company address
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditBillingDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => {
              // TODO: Implement billing update when backend supports it
              toast({ title: 'Info', description: 'Billing update is not yet implemented in the backend' })
              setEditBillingDialogOpen(false)
            }}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
